import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import { CreateInfoModal } from './InfoPagesModal'
import Loader from '../../../common/components/Loader'
import { Info, Languages, Parent } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import {
  deleteInfo,
  getAllInfos,
  getAllParents,
  getInfo,
  publishInfo,
  unpublishInfo,
} from '../../../core/services/infos'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'

export const InfoPages: React.FC = (): JSX.Element => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'INFO' })

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [infos, setInfos] = useState<Info[]>([])
  const [parents, setParents] = useState<Parent[]>([])
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [currentInfo, setCurrentInfo] = useState<Info>()
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        if (allLanguagesResponse.status === 200) {
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const infoResponse = await getAllInfos({
        page,
        size,
        sortBy,
        sortDirection,
      })
      if (infoResponse.status === 200) {
        setTotalElemenets(infoResponse.data.totalElements)
        setInfos(infoResponse.data.content)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, sortBy, editionId, festivalName, sortDirection])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response2 = await getAllParents()
        setParents(response2.data)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [])

  const handlePublish = (isCheck: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await publishInfo(isCheck)
        getData()
        setIsCheck([])
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (isCheck: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await unpublishInfo(isCheck)
        getData()
        setIsCheck([])
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    const nextChecked = !isCheckAll
    setIsCheckAll(nextChecked)
    setIsCheck(nextChecked ? infos.map((inf) => inf.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        const { status } = await deleteInfo(isCheck)
        getData()
        setIsCheck([])
        if (status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),

      width: '16px',
    },
    {
      title: 'Title',
      accessor: 'title',
      render: (row) => (
        <div className="max-w-[260px] line-clamp-3">
          <span>{row.title}</span>
        </div>
      ),
    },
    {
      title: 'Image (890x330)',
      accessor: '',
      render: (row) => (
        <img
          src={row.imageLink}
          className="rounded-[10px] h-[65px] w-[130px] object-contain"
        />
      ),
    },
    {
      title: 'Published Date',
      accessor: 'publishedDate',
      sortable: true,
      render: (row) =>
        formatDateTime(new Date(row.publishedDate).toISOString()),
    },
    {
      title: 'Ordinal',
      accessor: 'ordinal',
      sortable: true,
      render: (row) => (
        <Input
          value={row.ordinal}
          readOnly
          className="bg-monochrome-4 border-none"
        />
      ),
    },
    {
      title: 'Main Category',
      accessor: 'name',
      sortable: false,
      render: (row) => row?.parent?.name,
      width: '20%',
    },
    {
      title: 'Display',
      width: '30%',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col gap-2 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <Checkbox checked={row.mainMenuDisplay} disabled />
            <span className="text-sm text-monochrome-2">Main Menu</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={row.footerDisplay} disabled />
            <span className="text-sm text-monochrome-2">Footer Menu</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={row.registerBuyDisplay} disabled />
            <span className="text-sm text-monochrome-2">
              Register Buy Display
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Published',
      accessor: '',
      render: (row) => {
        return (
          <Checkbox
            onCheckedChange={() => {
              if (row.published) {
                handleUnpublish([row.id])
              } else {
                handlePublish([row.id])
              }
            }}
            checked={row.published}
            disabled={!hasEditPermission}
          />
        )
      },
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (event) => {
            event.preventDefault()
            if (!hasEditPermission) {
              return
            }
            try {
              const response = await getInfo(row.id)
              setCurrentInfo(response.data)
              setShowCreateModal(true)
            } catch (e) {
              console.error(e)
            }
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Info Pages"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setShowCreateModal(true)
                  setCurrentInfo(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Info Pages" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          isCheck={isCheck}
          columns={columns}
          handleSortBy={handleSortBy}
          data={infos}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateInfoModal
        info={currentInfo}
        setShowCreateModal={setShowCreateModal}
        showCreateModal={showCreateModal}
        size={size}
        setTotalElements={setTotalElemenets}
        setInfos={setInfos}
        setCurrentInfo={setCurrentInfo}
        page={page + 1}
        parents={parents}
        allLanguages={allLanguages}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Info'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
